import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import logger from '../../utils/logger';
import { format } from 'date-fns';
import ContractsTable from './ContractsTable';
import MonthlyCalculator from './components/MonthlyCalculator';
import { useDashboardLogic, FILTER_MODES, determineContractType, freshContractRow } from './contracts.logic';
import { exportCell, salikCountOf } from './utils/contractRows';
import { netBalanceExport } from './utils/liveBalance';
import { ejarFillJob } from '../../services/ejarFillJob';
import RequirePermission from '../../components/RequirePermission';
import LoadingSpinner from '../../components/LoadingSpinner';
import { PERMISSIONS } from '../../constants/permissions';
import { contractsService } from '../../services/firestore';
import { errorMessage, finesService } from '../../services/finesService';
import { useAuth } from '../../contexts/AuthContext';
import ReturnCarsCalendar from './components/ReturnCarsCalendar';
import * as XLSX from 'xlsx';

import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import Badge from '../../ui/Badge';
import Card from '../../ui/Card';
import { ICONS } from '../../ui/icon-map';
import Tabs from '../../ui/Tabs/Tabs';
import ActionMenu from '../../ui/ActionMenu';
import { Input, Select } from '../../ui/Field/Field';
import { toast, confirmDialog } from '../../ui/feedback';
import { usePermissions } from '../../hooks/usePermissions';
import Layout from '../../components/Layout';
import NetworkStatus from '../../components/NetworkStatus';
import AuthStatus from '../../components/AuthStatus';
import { isOpenStatus } from '../../services/contractRules';
import { phoneDigits, formatPhone } from '../../utils/phoneUtils';
import { contractDateCell } from './ContractsTable';
import { contractDateSheetCell, fitContractDateColumns } from '../../utils/excelDates';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { EDIT_MODE_FIELDS, STAFF_SOURCE, contractNoOf, loadContractStaff, saveStaffFields, withStoredStaff } from '../../services/contractStaff';
import { mustSwitchBack, skipSwitchBackChanges } from './utils/switchBack';
import { saveEditsToStore } from './utils/storeEditMode';
import { dubaiTime } from '../../utils/dubaiTime';

// The contracts store (REACT_APP_CONTRACTS_V2): Edit Mode changes only what staff own on a contract (the Invygo car
// typed by hand); the rest of a contract is Ejar's and is changed in Ejar.
const STORE_EDIT_COLUMNS = Object.keys(EDIT_MODE_FIELDS);

// The Excel's Pickup / Dropoff Date: real Excel dates shown dd/mm/yyyy hh:mm (read the way the table shows
// them, the old "dd-mm-yyyyHHMM" too), so they sort and filter by date; a value that cannot be read as before.
const CONTRACT_DATE_KEYS = new Set(['pickupDate', 'dropoffDate']);
const exportValue = (key, val) => (CONTRACT_DATE_KEYS.has(key)
    ? contractDateSheetCell(contractDateCell(val), exportCell(val))
    : exportCell(val));
// The Excel's balance columns: an Invygo contract's 0 is '—' there too, as in the Net Balance column - not a paid 0
// (utils/liveBalance netBalanceExport - owner, 28 Sep 2026).
const balanceCell = netBalanceExport;

// The tables' columns and their titles. Module constants, not built in the page: a new object on every draw made the
// memoised table draw all its rows again on each change of the page - a tab count arriving, a key typed in the
// search box - about 1 s each time (owner, 28 Sep 2026: "the Monthly page takes about 4 seconds").
const headersConfig = {
    open: ['contractNo', 'bookingNumber', 'source', 'openedBy', 'customer', 'invygoModel', 'invygoPlate', 'ejarModel', 'ejarPlate', 'phoneNumber', 'pickupDate', 'dropoffDate', 'netBalance'],
    master: ['contractNo', 'contractType', 'bookingNumber', 'source', 'openedBy', 'customer', 'invygoModel', 'invygoPlate', 'ejarModel', 'ejarPlate', 'model1', 'phoneNumber', 'pickupBranch', 'pickupDate', 'replacementDate', 'dropoffDate', 'contact', 'netBalance'],
    mismatch: ['contractNo', 'bookingNumber', 'source', 'openedBy', 'customer', 'invygoModel', 'invygoPlate', 'ejarModel', 'ejarPlate', 'phoneNumber', 'pickupDate', 'dropoffDate', 'issue', 'netBalance'],
    switchback: ['contractNo', 'bookingNumber', 'source', 'openedBy', 'customer', 'invygoModel', 'invygoPlate', 'ejarModel', 'ejarPlate', 'phoneNumber', 'pickupDate', 'dropoffDate', 'netBalance']
};

const headerDisplayNames = {
    contractNo: 'Contract No.', revenueDate: 'Revenue Date', bookingNumber: 'Booking', customer: 'Customer',
    invygoModel: 'Invygo Model', invygoPlate: 'Invygo Plate', ejarModel: 'Ejar Model',
    ejarPlate: 'Ejar Plate', phoneNumber: 'Phone Number', pickupBranch: 'Pick-up Branch',
    pickupDate: 'Pick-up Date', replacementDate: 'Replacement Date', dropoffDate: 'Drop-off Date',
    model1: 'Model (Repeated)', contact: 'Contact', contractType: 'Contract Type',
    issue: 'Issue', netBalance: 'Net Balance', source: 'Source', openedBy: 'Opened By'
};

export default function ContractsManagement() {
    const { currentUser } = useAuth();
    const { hasPermission } = usePermissions();
    const {
        loading, error,
        filterMode, setFilterMode,
        contractTypeFilter, setContractTypeFilter,
        debouncedSearchTerm, setDebouncedSearchTerm,
        filteredData,
        duplicatedContractsInfo,
        maintenanceData,
        isCarExpired,
        getExpiryDate,
        switchBackOf,
        inMismatchOf,
        stats, statsPending,
        searchResults,
        refreshContracts, updateLocalData, lastSync, searching,
        openContracts
    } = useDashboardLogic();

    // The main table shows every row (no pagination): the whole list fits one scrolling page.

    // Local UI state
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");

    // Sync URL Search with State on Mount
    useEffect(() => {
        const query = searchParams.get('search');
        if (query) {
            setSearchTerm(query);
            setDebouncedSearchTerm(query);
        }
    }, [searchParams, setDebouncedSearchTerm]);

    // Deep-link: ?status=open pre-selects the open-contracts filter (read once, on mount).
    useEffect(() => {
        if (searchParams.get('status') === 'open') {
            setFilterMode(FILTER_MODES.ALL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [activeMenuTable, setActiveMenuTable] = useState(null); // 'main', 'open', 'closed'
    const [visibleColumns, setVisibleColumns] = useState(() => {
        try {
            const saved = localStorage.getItem('contracts_visible_columns');
            if (!saved) return null;
            let cols = JSON.parse(saved);
            // columns added after a viewer saved a choice are shown to them once (5 Oct 2026: Source, Opened By)
            if (Array.isArray(cols) && localStorage.getItem('contracts_columns_added') !== '2026-10-05') {
                cols = [...new Set([...cols, 'source', 'openedBy'])];
                localStorage.setItem('contracts_visible_columns', JSON.stringify(cols));
                localStorage.setItem('contracts_columns_added', '2026-10-05');
            }
            return cols;
        } catch { return null; }
    });
    const [showColumnMenu, setShowColumnMenu] = useState(null);
    const handleToggleColumnMenu = useCallback((columnKey) => {
        setShowColumnMenu(prev => prev === columnKey ? null : columnKey);
    }, []);

    const [columnWidths, setColumnWidths] = useState({
        customer: 220, contractNo: 110, invygoPlate: 100,
        ejarPlate: 100, phoneNumber: 150, invygoModel: 150,
        ejarModel: 160, bookingNumber: 120, contractType: 110, source: 170, openedBy: 170,
        pickupBranch: 130, contact: 130, model1: 160, dropoffDate: 140,
        pickupDate: 140, replacementDate: 140, issue: 250
    });

    const [selectedContract, setSelectedContract] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState([]);
    const handleCancel = () => {
        setIsEditing(false);
        setEditedData([]);
    };
    // the rows as they were when Edit Mode opened: what a touched cell is compared with on save
    const editSnapshotRef = useRef(new Map());
    const handleEdit = () => {
        setIsEditing(true);
        const copy = JSON.parse(JSON.stringify(filteredData)); // Deep copy
        editSnapshotRef.current = new Map(JSON.parse(JSON.stringify(filteredData)).map((r) => [r.id, r]));
        setEditedData(copy);
    };

    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const changedItems = editedData.filter(item => item._dirty && item.id);

            if (changedItems.length === 0) {
                toast.info("No changes detected.");
                setIsEditing(false);
                setIsSaving(false);
                return;
            }

            // Process changes in parallel (chunks of 10 for safety)
            const CHUNK_SIZE = 10;
            const updatedBy = (currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'Unknown User'));
            
            // An Invygo Plate typed by hand becomes the contract's fingerprint (source
            // "manual") - the engine's hourly learner never overwrites it.
            const originalById = editSnapshotRef.current;
            const norm = (v) => String(v || '').replace(/\s+/g, '').toUpperCase();
            // ONLY the cells the user touched AND changed go to the save. Sending the whole row wrote back, from the
            // page's copy taken when it opened, the month rows, status and comments too - undoing a Fill from Ejar
            // made meanwhile or setting a closed contract back to "Open" (found in the 26 Sep 2026 audit). A field
            // the engine changes while the page is open is never sent, because the user did not touch it.
            // a cell edits text: "1200" over the number 1200 (or "" over nothing) is not a change
            const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)
                || ((a == null || a === '') && (b == null || b === ''))
                || (a != null && b != null && typeof a !== 'object' && typeof b !== 'object' && String(a) === String(b));
            const changesOf = (before, item) => Object.fromEntries((item._touched || [])
                .filter((k) => before && !same(item[k], before[k]))
                .map((k) => [k, item[k]]));
            if (USE_CONTRACTS_V2) {
                // The contracts store: only the Invygo car typed by hand is staff data (the other cells are not
                // editable - Ejar is their source). Each contract is saved on its version as it is now; the
                // store marks a typed plate as the manual fingerprint itself.
                const { saved, failed, ignored } = await saveEditsToStore(changedItems,
                    (item) => changesOf(originalById.get(item.id), item), { chunkSize: CHUNK_SIZE });
                if (saved.length) updateLocalData(saved.map(({ _dirty, _touched, ...rest }) => rest));
                if (ignored.length) toast.warning(`Not saved - Ejar is the source of: ${ignored.map((k) => headerDisplayNames[k] || k).join(', ')}. Change them in Ejar.`);
                if (failed.length) {
                    // what was saved is saved; Edit Mode stays open with the rest, to be saved again
                    const shown = failed.slice(0, 3).map(({ item, error }) => `${contractNoOf(item)}: ${error?.message || error}`);
                    toast.error(`${failed.length} not saved${saved.length ? ` (${saved.length} saved)` : ''} - ${shown.join(' · ')}${failed.length > 3 ? ' …' : ''}`);
                    return;
                }
                setIsEditing(false);
                if (saved.length) toast.success(`Saved ${saved.length} changes successfully!`);
                else if (!ignored.length) toast.info('No changes detected.');
                return;
            }
            for (let i = 0; i < changedItems.length; i += CHUNK_SIZE) {
                const chunk = changedItems.slice(i, i + CHUNK_SIZE);
                await Promise.all(chunk.map(item => {
                    const before = originalById.get(item.id);
                    const changes = changesOf(before, item);
                    if (!Object.keys(changes).length) return null;
                    const plateChanged = !!before && norm(before.invygoPlate) !== norm(item.invygoPlate);
                    return contractsService.updateContract(item.id, plateChanged ? { ...changes, _invygoPlateChanged: true } : changes, updatedBy, 'Table');
                }));
            }

            // Strip _dirty flag before updating local state to prevent future unnecessary saves
            const cleanedData = editedData.map(item => {
                const { _dirty, _touched, ...rest } = item;
                return rest;
            });
            updateLocalData(cleanedData); // Optimistic Update
            setIsEditing(false);
            toast.success(`Saved ${changedItems.length} changes successfully!`);
        } catch (err) {
            toast.error("Error saving changes: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (visibleColumns) {
            try { localStorage.setItem('contracts_visible_columns', JSON.stringify(visibleColumns)); } catch { /* storage blocked or full */ }
        }
    }, [visibleColumns]);

    // Debounce search - DISABLED (User prefers Enter key)
    /*
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, setDebouncedSearchTerm]);
    */

    const handleColumnResize = useCallback((headerKey, newWidth) => {
        setColumnWidths(prev => ({ ...prev, [headerKey]: newWidth }));
    }, []);

    // Non-blocking notification (shared toast system). type: info | success | warning | error
    const showToastNotification = useCallback((msg, type = 'info') => {
        (toast[type] || toast.info)(msg);
    }, []);

    // Skip a switch back (owner, 4 Nov 2026: "اوبشن تخطى فى حالة عدم الرغبة فى السويتش باك" - never an Invygo contract,
    // whose switch back is a must): the customer keeps the car they have now, which becomes the contract's own car -
    // saved as Edit Mode saves a typed Invygo car, so the contract leaves Mismatch and Switch Back
    const skipSwitchBack = useCallback(async (row) => {
        if (mustSwitchBack(row)) return;
        const changes = skipSwitchBackChanges(row);
        const no = contractNoOf(row);
        if (!(await confirmDialog({
            title: 'Skip the switch back?',
            message: `${row.customer || no} keeps ${[row.ejarModel, row.ejarPlate].filter(Boolean).join(' - ')}: it becomes the contract's own car and ${no} leaves Mismatch and Switch Back. A later switch on Ejar shows here again.`,
            confirmLabel: 'Skip switch back',
        }))) return;
        try {
            await saveStaffFields(no, { invygo_plate: changes.invygoPlate, invygo_model: changes.invygoModel }, STAFF_SOURCE.table);
            updateLocalData([{ id: row.id, contractNo: row.contractNo, ...changes, invygo_plate: changes.invygoPlate,
                invygo_model: changes.invygoModel, 'Invygo Plate': changes.invygoPlate, 'Invygo Model': changes.invygoModel }]);
            toast.success(`Switch back skipped - ${no}`);
        } catch (e) {
            toast.error(`Not saved - ${no}: ${errorMessage(e)}`);
        }
    }, [updateLocalData]);
    const onSkipSwitchBack = USE_CONTRACTS_V2 && hasPermission(PERMISSIONS.edit_contracts) ? skipSwitchBack : undefined;

    const handleCopy = useCallback((text, msg) => {
        // navigator.clipboard rejects when the document is not focused (a click on a
        // link that opens a new tab steals focus) or is unavailable: fall back to the
        // classic hidden-textarea + execCommand copy, which works on a user click.
        const legacyCopy = () => {
            try {
                const ta = document.createElement('textarea');
                ta.value = String(text ?? '');
                ta.setAttribute('readonly', '');
                ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.left = '0'; ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select(); ta.setSelectionRange(0, ta.value.length);
                const ok = document.execCommand('copy');
                document.body.removeChild(ta);
                return ok;
            } catch (e) { return false; }
        };
        const done = () => showToastNotification(msg || 'Copied!', 'success');
        const fail = (err) => { logger.error('Copy failed', err); showToastNotification('Copy failed', 'error'); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(String(text ?? '')).then(done).catch((err) => (legacyCopy() ? done() : fail(err)));
        } else {
            legacyCopy() ? done() : fail(new Error('clipboard unavailable'));
        }
    }, [showToastNotification]);

    // --- Fetch missing phone numbers from Ejar (same flow as the airport page) ---
    // Some contracts (imported by the hourly Ejar export) carry no phone until the
    // nightly Contracts Report catches up. This button triggers the engine's Layer-2
    // fill (fetch phone/identity from each open contract's Ejar detail now), then
    // polls so the "missing" counter falls as the numbers actually arrive.

    // --- Fill ALL open monthly Business Bay contracts from Ejar in one go ---
    // Same work as the calculator's "Fill from Ejar" button, for every contract:
    // the engine reads each contract's Ejar detail in a background job, and this
    // page distributes payments / Salik / parking / other / fines into the month
    // rows (fillRowsFromEjar) and saves them as the signed-in user. Rows are
    // generated first when a contract has none (syncMissingRowsForOpenContracts).
    const monthlyBBContracts = useMemo(() => (openContracts || []).filter((r) => {
        if (!isOpenStatus(r)) return false;   // a blank status is not open
        // Business Bay Branch (D03) only - D04 / "Business Bay - Invygo" contracts are
        // typed Invygo by determineContractType and drop out here.
        const branch = String(r.pickupBranch || r['Pick-up Branch'] || '').toLowerCase();
        // (Ejar's "Type" column says "Spot" for walk-in contracts of every kind - it
        // is NOT the rental type, so it is not used here.)
        if (!branch.includes('business bay')) return false;
        return determineContractType(r) === 'Monthly';
    }), [openContracts]);
    // The engine does the whole job (reads Ejar, fills the months, saves): this page only starts it;
    // services/ejarFillJob follows it and <EjarFillStatus /> shows it on every page, in every session.
    const [fillJob, setFillJob] = useState(() => ejarFillJob.get());
    useEffect(() => ejarFillJob.subscribe(setFillJob), []);
    useEffect(() => ejarFillJob.onFinished(refreshContracts), [refreshContracts]);
    const fillLocal = fillJob.local;
    const fillBusy = ejarFillJob.isRunning() || !!fillJob.remote;
    // "Last fill 25/09 15:40 by naseer · 12 saved · 80 unchanged · 12 not changed on Ejar"
    const lastFill = fillJob.last ? (() => {
        const l = fillJob.last;
        const at = String(l.finishedAt || '');
        const when = at ? `${at.slice(8, 10)}/${at.slice(5, 7)} ${at.slice(11, 16)}` : '?';
        return `Last fill ${when} by ${String(l.startedBy || '?').split('@')[0]} · ${l.saved || 0} saved · ${l.unchanged || 0} unchanged · ${l.skipped || 0} not changed on Ejar`;
    })() : 'No fill yet';

    const handleFillAllFromEjar = useCallback(async () => {
        // the engine's job saves where the pages read: the contracts store under FINES_WRITE_PG (store_writes.fill_from_ejar
        // - owner, 4 Oct 2026: "افتحه"; it was shut while the job still wrote Firestore)
        if (ejarFillJob.isRunning()) return;
        const busy = await ejarFillJob.refreshRemote();
        if (busy) {
            showToastNotification(`A Fill from Ejar is already running - started by ${String(busy.startedBy || '?').split('@')[0]} at ${String(busy.startedAt || '').slice(11, 16)} (${busy.applied || 0}/${busy.total} saved).`, 'warning');
            return;
        }
        const targets = monthlyBBContracts;
        if (!targets.length) { showToastNotification('No open monthly Business Bay contracts.'); return; }
        if (!(await confirmDialog({
            title: 'Fill from Ejar?',
            message: `Fill ${targets.length} open monthly Business Bay contract(s) from Ejar?
${lastFill}.
Only contracts Ejar changed since their last fill (or not filled for a day) are read again; an unchanged result is not rewritten.
Payments, Salik, Parking, Other and Fines are refilled per month; rent / dates stay as they are.
The engine does it all - you can open other pages or close this one.`,
            confirmLabel: 'Fill from Ejar',
        }))) return;
        const updatedBy = (currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'Unknown User'));
        try { await ejarFillJob.start(targets, updatedBy); }
        catch (e) { showToastNotification(`Fill from Ejar: ${errorMessage(e)}`, 'error'); }
    }, [monthlyBBContracts, currentUser, showToastNotification, lastFill]);

    // Header helpers: headersConfig / headerDisplayNames (module constants, below the imports)
    const currentHeaders = useMemo(() => {
        if (filterMode === FILTER_MODES.MISMATCH) return headersConfig.mismatch;
        if (filterMode === FILTER_MODES.SWITCHBACK && debouncedSearchTerm.trim() === '') return headersConfig.switchback;
        if (debouncedSearchTerm.trim() === '' && (filterMode === FILTER_MODES.ALL || filterMode === FILTER_MODES.EXPIRED || filterMode === FILTER_MODES.NO_RENT || filterMode === FILTER_MODES.UNPAID)) return headersConfig.open;

        // Determine headers based on data presence if searching
        if (filteredData.length === 0) return headersConfig.open;
        const populated = new Set(['contractNo']);
        filteredData.forEach(row => {
            Object.keys(row).forEach(k => {
                if (row[k]) populated.add(k);
            });
        });
        if (debouncedSearchTerm) populated.add('contractType');
        return headersConfig.master.filter(k => populated.has(k));
    }, [filterMode, debouncedSearchTerm, filteredData]); // eslint-disable-line react-hooks/exhaustive-deps




    // Stable Handlers to prevent re-renders during typing
    const handleToggleMenuOpen = useCallback(() => { setActiveMenuTable(prev => prev === 'open' ? null : 'open'); }, []);
    const handleToggleMenuClosed = useCallback(() => { setActiveMenuTable(prev => prev === 'closed' ? null : 'closed'); }, []);
    // const handleToggleMenuMain = useCallback(() => { setActiveMenuTable(prev => prev === 'main' ? null : 'main'); }, []);

    const handleShowMenuNull = useCallback((val) => { if (!val) setActiveMenuTable(null); }, []);

    // The calculator opens from the contract as it is on the server now (one read), not from the
    // page's copy: a Fill from Ejar made while this page stayed open would otherwise be written back
    // over by the calculator's Save (CON-03). The table row is refreshed with it too.
    const openingRef = useRef(false);
    // The contracts store (REACT_APP_CONTRACTS_V2): the contract (Ejar's facts) and its staff data - the months, the
    // header fields, the notes, the History and the version its Save names - both from the store, read as it opens.
    // `openedFrom`: the page row it was opened from (opened again from it after a refused save); `calcKey` gives
    // each opening a fresh calculator.
    const [calcKey, setCalcKey] = useState(0);
    const openedFromRef = useRef(null);
    const openFromStore = useCallback(async (c, { fill = true } = {}) => {
        try {
            // the engine first brings the months up to Ejar (paid, Salik, parking, fines, other - owner, 14 Oct 2026);
            // when Ejar is slow or refuses, the calculator opens on the months as they were and says so
            if (fill && contractNoOf(c)) {
                const slow = setTimeout(() => toast.info(`Reading ${contractNoOf(c) || 'the contract'} from Ejar…`), 1500);
                try {
                    await finesService.ejarContractFill(contractNoOf(c));
                } catch (err) {
                    logger.warn('Ejar fill on open failed - opening the saved months', err);
                    toast.warning(`Could not refresh ${contractNoOf(c) || 'the contract'} from Ejar - showing the last saved figures (${errorMessage(err)})`);
                } finally {
                    clearTimeout(slow);
                }
            }
            const [fresh, staff] = await Promise.all([freshContractRow(c), loadContractStaff(contractNoOf(c))]);
            // the table row takes the newer copy at once (owner, 4 Oct 2026: the calculator said 3,944.43 while the
            // row still showed -1,905.57 from the page's copy) - as the Firestore path always did
            if (fresh !== c && fresh._last_updated !== c._last_updated) updateLocalData([fresh]);
            openedFromRef.current = c;
            setSelectedContract(withStoredStaff(fresh, staff));
            setCalcKey((k) => k + 1);
            setShowModal(true);
        } catch (err) {
            logger.error('Could not open the contract from the contracts store', err);
            toast.error(`Could not open ${contractNoOf(c) || 'the contract'}: ${err.message}`);
        }
    }, [updateLocalData]);
    const reopenFromStore = useCallback(() => {
        if (openedFromRef.current) openFromStore(openedFromRef.current, { fill: false });   // after a refused save
    }, [openFromStore]);
    const handleCustomerClick = useCallback(async (c) => {
        if (openingRef.current) return;
        openingRef.current = true;
        try {
            if (USE_CONTRACTS_V2) { await openFromStore(c); return; }
            const fresh = await freshContractRow(c);
            if (fresh !== c && fresh._last_updated !== c._last_updated) updateLocalData([fresh]);
            setSelectedContract(fresh);
            setShowModal(true);
        } finally {
            openingRef.current = false;
        }
    }, [updateLocalData, openFromStore]);



    const handleExport = useCallback(() => {
        if (!filteredData || filteredData.length === 0) {
            toast.info("No data to export.");
            return;
        }

        // Define columns for Summary sheet
        const summaryColumns = [
            { header: 'Contract No', key: 'contractNo' },
            { header: 'Contract Type', key: 'contractType' },
            { header: 'Booking No', key: 'bookingNumber' },
            { header: 'Source', key: 'source' },
            { header: 'Opened By', key: 'openedBy' },
            { header: 'Customer', key: 'customer' },
            { header: 'Phone', key: 'phoneNumber' },
            { header: 'Invygo Plate', key: 'invygoPlate' },
            { header: 'Ejar Plate', key: 'ejarPlate' },
            { header: 'Pickup Date', key: 'pickupDate' },
            { header: 'Dropoff Date', key: 'dropoffDate' },
            { header: 'Car Model (Invygo)', key: 'invygoModel' },
            { header: 'Car Model (Ejar)', key: 'ejarModel' },
            { header: 'Pickup Branch', key: 'pickupBranch' },
            { header: 'Status', key: 'status' },
            { header: 'Overall Net Balance', key: 'netBalance' }
        ];

        // Define columns for Detailed sheet
        const detailedColumns = [
            ...summaryColumns.slice(0, summaryColumns.length - 1), // All except Net Balance
            { header: 'Calculation Period Start', key: 'calcStart' },
            { header: 'Calculation Period End', key: 'calcEnd' },
            { header: 'Rent Amount', key: 'calcRent' },
            { header: 'Salik Amount', key: 'calcSalik' },
            { header: 'Salik Count', key: 'calcSalikCount' },
            { header: 'Traffic Fines Amount', key: 'calcFine' },
            { header: 'Traffic Fines Count', key: 'calcFineCount' },
            { header: 'Parking Amount', key: 'calcParking' },
            { header: 'Other Charges Amount', key: 'calcOther' },
            { header: 'Other Charges Count', key: 'calcOtherCount' },
            { header: 'Amount Paid', key: 'calcPaid' },
            { header: 'Contract Overall Balance', key: 'netBalance' }
        ];

        try {
            // 1. Prepare Summary Data
            const summaryData = filteredData.map(row => {
                const data = {};
                summaryColumns.forEach(col => {
                    let val = row[col.key];
                    if (col.key === 'contractType') {
                        val = row.contractType || determineContractType(row);
                    }
                    if (col.key === 'phoneNumber') val = formatPhone(phoneDigits(val));   // with the country code
                    if (col.key === 'netBalance') val = balanceCell(row);
                    data[col.header] = exportValue(col.key, val);
                });
                return data;
            });

            // 2. Prepare Detailed Data
            const detailedData = [];
            filteredData.forEach(row => {
                const months = Array.isArray(row.monthly_calculations) && row.monthly_calculations.length > 0 
                    ? row.monthly_calculations 
                    : [{}];
                
                months.forEach((calc) => {
                    let fineCount = 0;
                    if (Array.isArray(calc.fines_list)) fineCount = calc.fines_list.length;
                    else if (parseFloat(calc.fine) > 0) fineCount = '1+';

                    let otherCount = 0;
                    if (Array.isArray(calc.other_list)) otherCount = calc.other_list.length;
                    else if (parseFloat(calc.other) > 0) otherCount = '1+';

                    const salikCount = salikCountOf(calc);

                    const data = {};
                    detailedColumns.forEach(col => {
                        let val;
                        if (col.key === 'calcStart') val = calc.start;
                        else if (col.key === 'calcEnd') val = calc.end;
                        else if (col.key === 'calcRent') val = calc.totalRent;
                        else if (col.key === 'calcSalik') val = calc.salik;
                        else if (col.key === 'calcSalikCount') val = salikCount;
                        else if (col.key === 'calcFine') val = calc.fine;
                        else if (col.key === 'calcFineCount') val = fineCount;
                        else if (col.key === 'calcParking') val = calc.parking;
                        else if (col.key === 'calcOther') val = calc.other;
                        else if (col.key === 'calcOtherCount') val = otherCount;
                        else if (col.key === 'calcPaid') val = calc.paid;
                        else if (col.key === 'contractType') val = row.contractType || determineContractType(row);
                        else if (col.key === 'netBalance') val = balanceCell(row);
                        else val = row[col.key];

                        data[col.header] = exportValue(col.key, val);
                    });
                    detailedData.push(data);
                });
            });

            const wb = XLSX.utils.book_new();
            const wsSummary = fitContractDateColumns(XLSX.utils.json_to_sheet(summaryData));
            XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
            const wsDetailed = fitContractDateColumns(XLSX.utils.json_to_sheet(detailedData));
            XLSX.utils.book_append_sheet(wb, wsDetailed, "Calculations Detail");

            const timestamp = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Contracts_Report_${timestamp}.xlsx`);

            toast.success(`Exported ${filteredData.length} records to Excel.`);
            logger.info('Excel report exported successfully with two sheets');
        } catch (err) {
            logger.error("Export Error:", err);
            toast.error("Failed to export data.");
        }
    }, [filteredData]);

    // Filter modes: one compact segmented group with counts (same modes, permissions and handlers as before)
    const filterTabs = [
        { id: FILTER_MODES.ALL, label: 'All Contracts', count: stats.open, tone: 'neutral', permission: PERMISSIONS.view_contracts_tab_all },
        { id: FILTER_MODES.MISMATCH, label: 'Mismatch', count: stats.mismatch, tone: 'warning', permission: PERMISSIONS.view_contracts_tab_mismatch },
        { id: FILTER_MODES.SWITCHBACK, label: 'Switch Back', count: stats.switchback, tone: 'success', permission: PERMISSIONS.view_contracts_tab_switchback },
        { id: FILTER_MODES.EXPIRED, label: 'Expired Cars', count: stats.expired, tone: 'danger', permission: PERMISSIONS.view_contracts_tab_expired },
        // each tab its own permission (permissions phase 3, 4 Oct 2026; they followed All Contracts until the upgrade)
        { id: FILTER_MODES.BOOKING_EXP, label: 'Booking Expired', count: stats.bookingExp, tone: 'danger', permission: 'view_contracts_tab_booking_expired' },
        { id: FILTER_MODES.LATE_PAYMENT, label: 'Late Payment', count: stats.latePayment, tone: 'warning', permission: 'view_contracts_tab_late_payment' },
        { id: FILTER_MODES.STALE_KM, label: 'Stale KM', count: stats.staleKm, tone: 'warning', permission: 'view_contracts_tab_stale_km' },
        { id: FILTER_MODES.NO_RENT, label: 'No Rent', count: stats.noRent, tone: 'danger', permission: 'view_contracts_tab_no_rent' },
        { id: FILTER_MODES.UNPAID, label: 'Unpaid', count: stats.unpaid, tone: 'danger', permission: 'view_contracts_tab_unpaid' },
    ]
        .filter((t) => hasPermission(t.permission))
        .map((t) => ({
            id: t.id,
            label: (
                <>
                    {t.label}
                    <Badge size="sm" variant={t.count > 0 ? t.tone : 'neutral'}>{t.count}</Badge>
                </>
            ),
        }));
    // the page opens on All Contracts: a user without that tab (or a role shown without the tab in use) gets the first
    // tab they hold instead
    const allowedTabKey = filterTabs.map((t) => t.id).join('|');
    useEffect(() => {
        const allowed = allowedTabKey ? allowedTabKey.split('|') : [];
        if (allowed.length && !allowed.includes(filterMode)) setFilterMode(allowed[0]);
    }, [allowedTabKey, filterMode, setFilterMode]);

    const isAdminUser = currentUser?.permissions?.includes(PERMISSIONS.manage_roles) || currentUser?.role === 'admin';
    const lastSyncText = lastSync ? `Last sync ${dubaiTime(lastSync)}` : undefined;

    // Don't render main content if user is not authenticated
    if (!currentUser) {
        return (
            <Layout title="Monthly Management">
                <NetworkStatus />
                <AuthStatus />
                <div className="p-4 text-center">
                    <p className="text-gray-600">Sign in to open Monthly management.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Monthly Management" subtitle={lastSyncText}>
            <NetworkStatus />
            <AuthStatus />
            <div className="contracts-dashboard w-full">
                {showModal && selectedContract && (
                    <MonthlyCalculator
                        key={USE_CONTRACTS_V2 ? calcKey : undefined}
                        contract={selectedContract}
                        onClose={() => { setShowModal(false); setSelectedContract(null); }}
                        onSaveSuccess={updateLocalData}
                        onReload={USE_CONTRACTS_V2 ? reopenFromStore : undefined}
                        switchBackOf={switchBackOf}
                        onCopy={handleCopy}
                    />
                )}


                {/* Unified Controls Toolbar */}
                <Card padding="sm" className="mb-4 sticky top-0 z-30">
                    <div className="flex flex-col gap-3">
                        {/* Top Row: Search & Actions */}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            {/* Search */}
                            <div className="flex-1 min-w-[240px]">
                                <RequirePermission permission={PERMISSIONS.search_contracts}>
                                    <div className="relative">
                                        <Input
                                            type="text"
                                            aria-label="Search contracts (press Enter)"
                                            placeholder="Search: contract · booking · customer · Customer ID · phone (any format) · plate · date"
                                            prefix={<Icon glyph={ICONS.search} size="sm" />}
                                            className="w-full"
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    setDebouncedSearchTerm(searchTerm);
                                                }
                                            }}
                                        />
                                        {searchTerm && (
                                            <button
                                                type="button"
                                                onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                                                aria-label="Clear search"
                                            >
                                                <Icon glyph={ICONS.close} size="sm" />
                                            </button>
                                        )}
                                    </div>
                                </RequirePermission>
                            </div>

                            {/* Contract Type Filter */}
                            <div className="flex items-center">
                                <Select
                                    aria-label="Contract type"
                                    value={contractTypeFilter}
                                    onChange={(e) => setContractTypeFilter(e.target.value)}
                                    options={[
                                        { value: 'all', label: 'Check All' },
                                        { value: 'daily', label: 'Daily' },
                                        { value: 'monthly', label: 'Monthly' },
                                        { value: 'leasing', label: 'Leasing' },
                                        { value: 'invygo', label: 'Invygo' },
                                    ]}
                                />
                            </div>

                            {/* Action Buttons: related actions grouped into menus */}
                            <div className="flex flex-wrap items-center gap-2">
                                <ActionMenu
                                    label="Export"
                                    icon={ICONS.download}
                                    size="sm"
                                    items={[
                                        {
                                            label: 'Export contracts',
                                            description: 'Filtered contracts to Excel (.xlsx)',
                                            icon: ICONS.spreadsheet,
                                            onClick: handleExport,
                                            disabled: !filteredData || filteredData.length === 0,
                                            hidden: !hasPermission(PERMISSIONS.export_contracts),
                                        },
                                        {
                                            label: 'Export cache',
                                            description: 'Download the local data cache',
                                            icon: ICONS.database,
                                            onClick: () => {
                                                import('../../services/jsonCacheService').then(({ jsonCacheService }) => {
                                                    jsonCacheService.exportCache();
                                                });
                                            },
                                            hidden: !hasPermission('export_contracts_cache'),
                                        },
                                    ]}
                                />

                                {/* the phones fill by themselves every hour (engine) and the page reads what changed on its own:
                                    only the Ejar fill is left, as a plain button */}
                                {hasPermission('contracts_fill_from_ejar') && (
                                    <Button size="sm" onClick={handleFillAllFromEjar} disabled={fillBusy}
                                        leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                                        title={`Payments, Salik, parking, other and fines for open monthly Business Bay contracts · ${lastFill}`}>
                                        {fillLocal && ejarFillJob.isRunning() ? `Ejar fill ${fillLocal.done}/${fillLocal.total}` : (fillJob.remote ? `Running · ${fillJob.remote.applied || 0}/${fillJob.remote.total} (${String(fillJob.remote.startedBy || '?').split('@')[0]})` : `Fill from Ejar (${monthlyBBContracts.length})`)}
                                    </Button>
                                )}

                                {isEditing && (
                                    <Button size="sm" variant="ghost" onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                )}

                                <RequirePermission permission={PERMISSIONS.edit_contracts}>
                                    <Button
                                        size="sm"
                                        variant={isEditing ? 'primary' : 'secondary'}
                                        onClick={isEditing ? handleSave : handleEdit}
                                        isLoading={isSaving}
                                        leadingIcon={<Icon glyph={isEditing ? ICONS.save : ICONS.edit} size="sm" />}
                                    >
                                        {isEditing ? (isSaving ? 'Saving...' : 'Save') : 'Edit Mode'}
                                    </Button>
                                </RequirePermission>
                            </div>
                        </div>

                        {/* Bottom Row: Filter modes */}
                        {filterTabs.length > 0 && (
                            <div className="overflow-x-auto pb-1 no-scrollbar">
                                <Tabs
                                    variant="segmented"
                                    label="Contract filters"
                                    idPrefix="contracts-filter"
                                    items={filterTabs}
                                    value={filterMode}
                                    onChange={setFilterMode}
                                />
                            </div>
                        )}
                    </div>
                </Card>

                {loading && <LoadingSpinner />}

                {filterMode === FILTER_MODES.BOOKING_EXP && !debouncedSearchTerm ? (
                    <ReturnCarsCalendar
                        data={filteredData}
                        onDayClick={(day) => {
                            const formattedSearch = format(day, 'dd/MM/yyyy');
                            setSearchTerm(formattedSearch);
                            setDebouncedSearchTerm(formattedSearch);
                        }}
                    />
                ) : (
                    <div className="flex-1 flex flex-col min-h-0">
                        {error && (
                            <div className="error-message bg-red-50 border border-red-200 rounded-lg p-4 mb-4" role="alert">
                                <h3 className="text-sm font-semibold text-red-800 mb-1">Could not load the data</h3>
                                <p className="text-sm text-red-700">{error.message || String(error)}</p>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    className="mt-3"
                                    onClick={() => window.location.reload()}
                                >
                                    Reload the page
                                </Button>
                            </div>
                        )}

                        {!loading && !error && (
                            <>
                                {debouncedSearchTerm ? (
                                    <>
                                        <div className="flex items-baseline gap-2 mb-2">
                                            <h3 className="ds-section-title">Open Contracts ({searchResults.openContractsFromSearch.length})</h3>
                                        </div>
                                        <ContractsTable
                                            className="h-auto mb-6"
                                            data={searchResults.openContractsFromSearch}
                                            headers={headersConfig.open}
                                            visibleColumns={visibleColumns}
                                            columnWidths={columnWidths}
                                            onColumnResize={handleColumnResize}
                                            onToggleColumnMenu={handleToggleMenuOpen}
                                            showColumnMenu={activeMenuTable === 'open'}
                                            setShowColumnMenu={handleShowMenuNull}
                                            setVisibleColumns={setVisibleColumns}
                                            isAdmin={isAdminUser}
                                            headerDisplayNames={headerDisplayNames}
                                            duplicatedContractsInfo={duplicatedContractsInfo}
                                            maintenanceData={maintenanceData}
                                            switchBackOf={switchBackOf}
                                            inMismatchOf={inMismatchOf}
                                            onSkipSwitchBack={onSkipSwitchBack}
                                            isCarExpired={isCarExpired}
                                            getExpiryDate={getExpiryDate}
                                            onCustomerClick={handleCustomerClick}
                                            onCopy={handleCopy}
                                        />

                                        {filterMode !== FILTER_MODES.BOOKING_EXP && (
                                            <>
                                                <div className="flex items-baseline gap-2 mb-2">
                                                    <h3 className="ds-section-title flex items-center gap-1.5">
                                                        <Icon glyph={ICONS.lock} size="sm" tone="muted" />
                                                        Closed Contracts ({searchResults.closedContractsFromSearch.length})
                                                    </h3>
                                                    {searching && <span className="text-xs" style={{ color: 'var(--text-3)' }}>Searching…</span>}
                                                </div>
                                                <ContractsTable
                                                    className="h-auto"
                                                    key="table-closed"
                                                    data={searchResults.closedContractsFromSearch}
                                                    headers={headersConfig.master}
                                                    visibleColumns={visibleColumns}
                                                    columnWidths={columnWidths}
                                                    onColumnResize={handleColumnResize}
                                                    onToggleColumnMenu={handleToggleMenuClosed}
                                                    showColumnMenu={activeMenuTable === 'closed'}
                                                    setShowColumnMenu={handleShowMenuNull}
                                                    setVisibleColumns={setVisibleColumns}
                                                    isAdmin={isAdminUser}
                                                    headerDisplayNames={headerDisplayNames}
                                                    duplicatedContractsInfo={duplicatedContractsInfo}
                                                    maintenanceData={maintenanceData}
                                                    switchBackOf={switchBackOf}
                                                    inMismatchOf={inMismatchOf}
                                                    isCarExpired={isCarExpired}
                                                    getExpiryDate={getExpiryDate}
                                                    onCustomerClick={handleCustomerClick}
                                                    onCopy={handleCopy}
                                                />
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="text-xs text-gray-500 tabular-nums mb-2">
                                            {filteredData.length} records
                                        </div>
                                        <ContractsTable
                                            key="table-main"
                                            fillViewport
                                            data={isEditing ? editedData : filteredData}
                                            // a worker tab whose rows are not counted yet: 'Counting…', not "No contracts found"
                                            pending={statsPending && filterMode !== FILTER_MODES.ALL}
                                            filterMode={filterMode} // Pass filter mode for conditional logic
                                            headers={currentHeaders}
                                            visibleColumns={visibleColumns}
                                            columnWidths={columnWidths}
                                            onColumnResize={handleColumnResize}
                                            onToggleColumnMenu={handleToggleColumnMenu}
                                            showColumnMenu={showColumnMenu}
                                            setShowColumnMenu={setShowColumnMenu}
                                            setVisibleColumns={setVisibleColumns}
                                            isAdmin={isAdminUser}
                                            headerDisplayNames={headerDisplayNames}
                                            duplicatedContractsInfo={duplicatedContractsInfo}
                                            maintenanceData={maintenanceData}
                                            switchBackOf={switchBackOf}
                                            inMismatchOf={inMismatchOf}
                                            onSkipSwitchBack={onSkipSwitchBack}
                                            isCarExpired={isCarExpired}
                                            getExpiryDate={getExpiryDate}
                                            onCustomerClick={handleCustomerClick}
                                            onCopy={handleCopy}
                                            isEditing={isEditing}
                                            editableColumns={USE_CONTRACTS_V2 ? STORE_EDIT_COLUMNS : undefined}
                                            editedData={editedData}
                                            setEditedData={setEditedData}
                                            start={0}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
